import { Sheet, SheetTitle, SheetHeader, SheetContent, SheetTrigger, SheetFooter } from "@/shared/ui/sheet.tsx";
import { Button } from "../ui/button.tsx";
import ImageSection from "./ImageSection";
import PostBodyForm from "./PostBodyForm";
import { useContext, useEffect, useState } from "react";
import { FeebackFormSchema, FeedbackFormType, PostBodyFormSchema, PostBodyFormType, SocialPostType } from "./types";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAddFeedback, useGetCatalog, useSchedulePost, useUpdatePostBody } from "./api/single-post-api";
import { format } from "date-fns";
import { DateTime } from "luxon"
import {
  Save, SignpostBig
} from "lucide-react";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms";
import FeedbackForm from "./FeedbackForm";
import { AuthContext, AuthContextType } from "@/features/app-layout/context";
import DateCheckDialog from "./DateCheckDialog";

type EditPostSheetProps = {
  location: "scheduled" | "review" | "discarded"
  variant: "scheduled" | "pending"
  trigger: JSX.Element,
  postDetails: SocialPostType['content'][number],
  eventDetails: {
    event_id: number,
    event_name: string,
    story_id: number
  },
  eventIndex?: number,
  onSchedule: (id: number) => void,
  onSaveImage?: (url: string) => void
}

const EditPostSheet = ({ eventDetails, postDetails, trigger, onSchedule, variant, location, onSaveImage }: EditPostSheetProps) => {

  const theme = useAtomValue(themeAtom)

  const [open, setOpen] = useState(false)

  const [image, setImage] = useState(postDetails.image_url)

  const { username } = useContext<AuthContextType>(AuthContext)

  useEffect(() => {
    setImage(postDetails.image_url)
  }, [postDetails])

  const saveImageHandler = (url: string) => {
    setImage(url)
    if (onSaveImage) {
      onSaveImage(url)
    }
  }

  const feedbackForm = useForm<FeedbackFormType>({
    resolver: zodResolver(FeebackFormSchema)
  })

  const { schedulePost } = useSchedulePost(location, onSchedule)

  const { catalogData } = useGetCatalog(open)

  const { addFeedback } = useAddFeedback()

  const { updatePostBody, isSuccess } = useUpdatePostBody()

  const easternDate = DateTime.fromISO(postDetails.post_date, {
    zone: "America/New_York"
  }).toJSDate()

  const form = useForm<PostBodyFormType>({
    resolver: zodResolver(PostBodyFormSchema),
    defaultValues: {
      post_id: postDetails.post_id,
      post_content: postDetails.post_content,
      hashtags: postDetails.tags,
      post_date: easternDate,
      post_time: postDetails.post_time,
      image_recommendation: postDetails.image_video_recommendation ?? "",
      catalogue: postDetails.catalogue ?? ""
    }
  })

  const savePostHandler = () => {
    form.handleSubmit(submitHandler, errorHandler)()
  }


  const submitHandler = (data: PostBodyFormType) => {
    console.log(data)
    updatePostBody({ ...data, post_date: format(data.post_date, "yyyy-MM-dd"), post_time: data.post_time.length < 8 ? `${data.post_time}:00` : data.post_time })
  }
  const errorHandler = (errors: FieldErrors<PostBodyFormType>) => {
    console.error("Form errors:", errors);
  };

  return (
    <Sheet open={open} onOpenChange={(val: boolean) => {
      if (!val) {
        feedbackForm.handleSubmit((data: FeedbackFormType) => {
          if ((data.text || data.stars) && username) {
            addFeedback({
              post_id: postDetails.post_id,
              rating: data.stars ? data.stars : null,
              comment: data.text ? data.text : null,
              user_name: username
            })
          }
        })()
      }
      setOpen(val)
    }}>
      <SheetTrigger asChild>
        {trigger}
      </SheetTrigger>
      <SheetContent className="w-[100vw] sm:w-[80vw] md:w-[75vw] h-[100vh] flex flex-col justify-between overflow-auto bg-gray-50">
        <SheetHeader>
          <SheetTitle className="text-xl font-montserrat font-medium">Edit post for {eventDetails.event_name}</SheetTitle>
        </SheetHeader>
        <div className="w-full flex flex-col md:flex-row gap-2 h-[90vh] overflow-auto">
          <ImageSection
            story_id={eventDetails.story_id}
            event_id={eventDetails.event_id}
            onSaveImage={saveImageHandler}
            content={postDetails}
            isSuccess={isSuccess}
            onSavePost={savePostHandler}
            post_id={postDetails.post_id}
            postForm={form}
            catalog={catalogData ? catalogData.catalogue : []}
            imgUrl={image}
          />
          <div className="relative w-full md:w-1/2 flex flex-col justify-between">
            <PostBodyForm postForm={form} catalog={catalogData ? catalogData.catalogue : []} />
            <FeedbackForm feedbackForm={feedbackForm} />
          </div>
        </div>
        <SheetFooter className="flex flex-row flex-wrap items-center gap-2 w-full">
          <Button type="button" style={{ backgroundColor: theme.primary }} className="flex items-center gap-2 max-sm:text-xs"
            onClick={savePostHandler}><Save /> Save changes</Button>
          {variant === "pending" && (
            (new Date(postDetails.post_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) ? <DateCheckDialog data={postDetails} onSchedule={onSchedule} location={location} /> :
              <Button
                disabled={postDetails.image_url === null}
                type="button"
                style={{ backgroundColor: theme.primary }} className="max-sm:text-xs flex items-center gap-2"
                onClick={() => {
                  schedulePost(postDetails.post_id)
                  setOpen(false)
                }}><SignpostBig /> Schedule Post</Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export default EditPostSheet;
